import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./FeaturedSeriesShowcase.module.css";

const FeaturedSeriesShowcase = ({ initailData }: any) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0); // 1 for next, -1 for prev
  const [hoveredCard, setHoveredCard] = useState(null);
  const [imageIndices, setImageIndices] = useState({});
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const featuredSeriesData = initailData || [];
  const carouselRef = useRef(null);
  const intervalRef = useRef(null);

  // Enhanced auto-play with pause on hover
  useEffect(() => {
    if (isAutoPlaying && featuredSeriesData.length > 0) {
      // @ts-ignore
      intervalRef.current = setInterval(() => {
        handleNext();
      }, 4000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoPlaying, featuredSeriesData.length]);

  const handlePrev = () => {
    setIsAutoPlaying(false);
    setDirection(-1);
    setActiveIndex(
      (prev) =>
        (prev - 1 + featuredSeriesData.length) % featuredSeriesData.length
    );
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setDirection(1);
    setActiveIndex((prev) => (prev + 1) % featuredSeriesData.length);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  // Get visible items - always show 3 items
  const getVisibleItems = () => {
    const items: any = [];
    const total = featuredSeriesData.length;

    if (total === 0) return items;

    // Always show 3 items: current, next, and previous for seamless loop
    for (let i = -1; i <= 1; i++) {
      const index = (activeIndex + i + total) % total;
      items.push({
        ...featuredSeriesData[index],
        position: i, // -1: left, 0: center, 1: right
        key: `${featuredSeriesData[index]?.id}-${i}`,
      });
    }

    return items;
  };

  const handleImageHover = (seriesId: any, totalImages: any) => {
    if (totalImages > 1) {
      setImageIndices((prev) => ({
        ...prev,
        // @ts-ignore
        [seriesId]: ((prev[seriesId] || 0) + 1) % totalImages,
      }));
    }
  };

  // Carousel animation variants
  const carouselVariants = {
    enter: (direction: any) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.8,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction: any) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.8,
    }),
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
    hover: {
      y: -15,
      scale: 1.02,
      transition: { duration: 0.3, ease: "easeOut" },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 1.1 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <section
      className={styles.featuredSection}
      aria-labelledby="featured-series-title"
    >
      <div className={styles.backgroundElements}>
        <div className={styles.floatingShape1}></div>
        <div className={styles.floatingShape2}></div>
      </div>

      <div className={styles.container}>
        {/* Section Header */}
        <motion.div
          className={styles.sectionHeader}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 id="featured-series-title" className={styles.sectionTitle}>
            Premium Collections
          </h2>
          <p className={styles.sectionSubtitle}>
            Discover our handcrafted furniture series designed for modern
            workspaces
          </p>
          <div className={styles.titleDecoration}>
            <div className={styles.decorationLine}></div>
            <div className={styles.decorationDot}></div>
            <div className={styles.decorationLine}></div>
          </div>
        </motion.div>

        {/* Carousel */}
        <div className={styles.carouselWrapper}>
          <button
            className={styles.navButton}
            onClick={handlePrev}
            aria-label="Previous collections"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 6L9 12L15 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div
            className={styles.carouselContainer}
            ref={carouselRef}
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <div className={styles.carouselTrack}>
              <AnimatePresence
                custom={direction}
                initial={false}
                mode="popLayout"
              >
                {getVisibleItems().map((item: any) => {
                  // @ts-ignore
                  const currentImageIndex = imageIndices[item?.id] || 0;
                  const currentModel = item?.models?.[currentImageIndex];
                  const totalImages = item?.models?.length || 0;

                  return (
                    <motion.div
                      key={item.key}
                      className={`${styles.seriesCard} ${
                        item.position === 0
                          ? styles.centerCard
                          : item.position === -1
                          ? styles.leftCard
                          : styles.rightCard
                      }`}
                      custom={direction}
                      variants={carouselVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{
                        duration: 0.6,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      onMouseEnter={() => setHoveredCard(item?.id)}
                      onMouseLeave={() => setHoveredCard(null)}
                    >
                      {/* Card Glow Effect */}
                      <div className={styles.cardGlow}></div>

                      {/* Image Container */}
                      <div
                        className={styles.imageContainer}
                        onMouseEnter={() =>
                          handleImageHover(item?.id, totalImages)
                        }
                      >
                        {currentModel?.picture?.[0]?.path && (
                          <motion.img
                            src={currentModel.picture[0].path}
                            alt={`${item?.title} - ${currentModel?.title}`}
                            className={styles.seriesImage}
                            // @ts-ignore
                            variants={imageVariants}
                            loading="lazy"
                          />
                        )}

                        {/* Image Overlay */}
                        <div className={styles.imageOverlay}>
                          <div className={styles.imageBadge}>
                            {totalImages} Models
                          </div>

                          {/* Model Name */}
                          <div className={styles.modelInfo}>
                            <span className={styles.modelName}>
                              {currentModel?.title}
                            </span>
                          </div>

                          {/* Image Pagination */}
                          {totalImages > 1 && (
                            <div className={styles.imagePagination}>
                              {Array.from({ length: totalImages }).map(
                                (_, idx) => (
                                  <button
                                    key={idx}
                                    className={`${styles.paginationDot} ${
                                      currentImageIndex === idx
                                        ? styles.active
                                        : ""
                                    }`}
                                    onClick={() =>
                                      setImageIndices((prev) => ({
                                        ...prev,
                                        [item?.id]: idx,
                                      }))
                                    }
                                    aria-label={`View model ${idx + 1}`}
                                  />
                                )
                              )}
                            </div>
                          )}
                        </div>

                        {/* Hover Action */}
                        <div className={styles.hoverAction}>
                          <span>View Gallery</span>
                        </div>
                      </div>

                      {/* Card Content */}
                      <div className={styles.cardContent}>
                        <div className={styles.cardHeader}>
                          <h3 className={styles.seriesTitle}>{item?.title}</h3>
                          <div className={styles.ratingBadge}>
                            <span className={styles.rating}>4.8</span>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                          </div>
                        </div>

                        <p className={styles.seriesDescription}>
                          {item?.description}
                        </p>

                        <div className={styles.cardFeatures}>
                          <div className={styles.feature}>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                            </svg>
                            Premium Quality
                          </div>
                          <div className={styles.feature}>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                            </svg>
                            Customizable
                          </div>
                        </div>

                        <div className={styles.cardFooter}>
                          <motion.a
                            href={`/series/${item?.id}`}
                            className={styles.primaryButton}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            Explore Collection
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M5 12H19M19 12L12 5M19 12L12 19"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </motion.a>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>

          <button
            className={styles.navButton}
            onClick={handleNext}
            aria-label="Next collections"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 6L15 12L9 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Progress Indicators */}
        <div className={styles.progressContainer}>
          {featuredSeriesData
            .slice(0, Math.min(6, featuredSeriesData.length))
            .map((_: any, index: any) => (
              <button
                key={index}
                className={`${styles.progressDot} ${
                  activeIndex === index ? styles.active : ""
                }`}
                onClick={() => {
                  setDirection(index > activeIndex ? 1 : -1);
                  setActiveIndex(index);
                }}
                aria-label={`Go to collection ${index + 1}`}
              />
            ))}
        </div>
      </div>
    </section>
  );
};

export default React.memo(FeaturedSeriesShowcase);
